
import { Check, Pencil, Scissors, Ruler, CircleOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

interface ToolUsageChecklistProps {
  usedTools: {
    draw: boolean;
    incision: boolean;
    ruler: boolean;
    angle: boolean;
  };
  isExpanded?: boolean;
}

// Helper for localStorage
const storeToolUsage = (usedTools: any): void => {
  try {
    localStorage.setItem('tool_usage_state', JSON.stringify(usedTools));
  } catch (e) {
    console.error("Error storing tool usage in localStorage:", e);
  }
};

export const ToolUsageChecklist = ({ usedTools, isExpanded = false }: ToolUsageChecklistProps) => {
  // Store tool usage in localStorage whenever it changes
  useEffect(() => {
    storeToolUsage(usedTools);
  }, [usedTools]);

  const checklistItems = [
    { 
      id: 'draw', 
      label: 'Skin Marker', 
      description: 'Mark measurements on the skin',
      completed: usedTools.draw,
      icon: Pencil 
    },
    { 
      id: 'incision', 
      label: 'Planned Incision', 
      description: 'Plan incision paths',
      completed: usedTools.incision,
      icon: Scissors 
    },
    { 
      id: 'ruler', 
      label: 'Measurements', 
      description: 'Add precise measurements',
      completed: usedTools.ruler,
      icon: Ruler 
    },
    { 
      id: 'angle', 
      label: 'Angles', 
      description: 'Mark and measure angles',
      completed: usedTools.angle,
      icon: () => (
        <span className="text-sm font-medium tracking-tight">A°</span>
      )
    },
  ];

  const totalSteps = checklistItems.length;
  const completedCount = Object.values(usedTools).filter(Boolean).length;
  const progressPercentage = (completedCount / totalSteps) * 100;

  return (
    <Card className={cn(
      "border-indigo-100/60 overflow-hidden",
      isExpanded ? "shadow-md" : "shadow-sm"
    )}>
      <CardHeader className={cn(
        "bg-gradient-to-r from-indigo-50/80 to-blue-50/60 pb-2", // Reduced pb from 3 to 2
        isExpanded ? "px-5 py-3" : "px-4 py-2" // Reduced padding
      )}>
        <div className="flex items-center justify-between mb-1">
          <CardTitle className={cn(
            "text-indigo-700 font-medium flex items-center gap-1.5",
            isExpanded ? "text-base" : "text-sm"
          )}>
            Pre-Analysis Checklist
            {completedCount === totalSteps && (
              <span className={cn(
                "inline-flex items-center gap-0.5 font-medium text-green-600 bg-green-50/70 rounded-full border border-green-200/60",
                isExpanded ? "text-xs px-1.5 py-0.5" : "text-[10px] px-1 py-0.5"
              )}>
                <Check className={isExpanded ? "h-3 w-3" : "h-2.5 w-2.5"} />
                <span>Complete!</span>
              </span>
            )}
          </CardTitle>
          <span className={cn(
            "text-indigo-500 font-semibold flex items-center gap-1",
            isExpanded ? "text-sm" : "text-xs"
          )}>
            {completedCount}/{totalSteps}
          </span>
        </div>

        {/* Progress bar */}
        <Progress 
          value={progressPercentage} 
          className={cn(
            "h-1.5 bg-indigo-100/40",
            completedCount === totalSteps ? "bg-green-100/40" : ""
          )} 
        />
      </CardHeader>

      <CardContent className={cn(
        isExpanded ? "px-5 py-3" : "px-4 py-2" // Reduced padding
      )}>
        <p className={cn(
          "text-gray-600 mb-2", // Reduced mb from 3 to 2
          isExpanded ? "text-sm" : "text-xs"
        )}>
          Use these tools for optimal AI analysis:
        </p>
        
        <div className="space-y-1"> {/* Reduced space-y from 2 to 1 */}
          {checklistItems.map((item) => (
            <div 
              key={item.id}
              className={cn(
                "flex items-center gap-3 p-1.5 rounded-lg transition-all duration-200", // Reduced p from 2 to 1.5
                item.completed 
                  ? "bg-indigo-50/50 border border-indigo-100/60" 
                  : "hover:bg-gray-50/60"
              )}
            >
              <div className={cn(
                "rounded-full flex items-center justify-center flex-shrink-0",
                item.completed 
                  ? "bg-indigo-100 text-indigo-600 border-2 border-indigo-200/60" 
                  : "border-2 border-gray-200",
                isExpanded ? "w-7 h-7" : "w-6 h-6"
              )}>
                {item.completed ? (
                  <Check className={isExpanded ? "w-4 h-4" : "w-3.5 h-3.5"} />
                ) : (
                  <item.icon className={isExpanded ? "w-4 h-4 text-gray-400" : "w-3.5 h-3.5 text-gray-400"} />
                )}
              </div>
              
              <div className="flex flex-col">
                <span className={cn(
                  "font-medium",
                  item.completed ? "text-indigo-700" : "text-gray-600",
                  isExpanded ? "text-sm" : "text-xs"
                )}>
                  {item.label}
                </span>
                
                {isExpanded && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    {item.description}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {completedCount < totalSteps && (
          <div className="mt-2 text-center"> {/* Reduced mt from 3 to 2 */}
            <span className={cn(
              "text-indigo-600 font-medium inline-block",
              isExpanded ? "text-xs" : "text-[10px]"
            )}>
              {completedCount === 0 ? 
                "Use at least one tool to start" : 
                `${totalSteps - completedCount} more ${(totalSteps - completedCount) === 1 ? 'tool' : 'tools'} for best results`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
